<template>
    <div class="relative w-full">
        <div
            v-show="isStart || isBoth"
            class="flex w-full flex-col gap-5"
        >
            <span
                v-if="!hideLabels"
                class="text-base font-medium text-muval-gray-1"
                >Pickup date</span
            >

            <div
                class="grid max-w-[300px] grid-flow-row grid-rows-2 gap-3 sm:max-w-[500px] sm:grid-flow-col sm:grid-cols-5 sm:grid-rows-1"
                :class="startTimeSuffix && 'sm:!max-w-[560px]'"
            >
                <div class="col-span-3 sm:col-span-2">
                    <MuvalSelect
                        placeholder="Pickup options"
                        :vid="keys.start_at_type"
                        displayLabel="label"
                        valueProp="value"
                        :selectOptions="dateOptions"
                    />
                </div>
                <template v-if="startType == 'BETWEEN'">
                    <div class="col-span-3">
                        <MPDatetime
                            :start-vid="keys.start_at_from"
                            :end-vid="keys.start_at_by"
                            is-range
                            :teleport="teleport"
                            :time-suffix="startTimeSuffix"
                            :class="startTimeSuffix ? 'w-full' : ''"
                            :min-date="allowPastDates ? null : new Date()"
                        >
                            <template #action-extra="slotProps">
                                <slot
                                    name="start-action-extra"
                                    v-bind="slotProps"
                                />
                            </template>
                        </MPDatetime>
                    </div>
                </template>
                <template v-if="startType == 'ON'">
                    <div class="col-span-3 flex gap-4">
                        <MPDatetime
                            :vid="keys.start_at_by"
                            :teleport="teleport"
                            :time-suffix="startTimeSuffix"
                            :class="startTimeSuffix ? 'w-full' : ''"
                            :min-date="allowPastDates ? null : new Date()"
                        >
                            <template #action-extra="slotProps">
                                <slot
                                    name="start-action-extra"
                                    v-bind="slotProps"
                                />
                            </template>
                        </MPDatetime>
                    </div>
                </template>
                <template v-if="startType === 'UNSURE'">
                    <div class="col-span-3 flex">
                        <MuvalSelect
                            :vid="keys.start_date_interval"
                            :single-select="true"
                            :selectOptions="moveDateIntervals"
                        />
                    </div>
                </template>
                <template v-if="startType == 'ON' && !noFlexibility">
                    <MuvalSelect
                        :vid="keys.start_date_flexibility"
                        :single-select="true"
                        :selectOptions="flexibilityOptions"
                        displayLabel="label"
                        valueProp="value"
                    />
                </template>
            </div>
        </div>

        <div
            v-if="isEnd || isBoth"
            class="flex w-full flex-col gap-5"
            :class="isBoth ? 'mt-5' : ''"
        >
            <div
                v-if="!hideLabels"
                class="flex gap-2"
            >
                <span
                    class="my-auto text-base font-medium text-muval-gray-1"
                    v-text="$props.deliveryLabel"
                />
                <MuvalTip
                    class="my-auto"
                    text="We will try to match you with options that suit your ideal dates. However, if availability or logistics constraints mean that results are limited, we may show you options that sit slightly outside these parameters."
                />
            </div>
            <div
                class="grid max-w-[300px] grid-flow-row grid-rows-2 gap-3 sm:max-w-[500px] sm:grid-flow-col sm:grid-cols-5 sm:grid-rows-1"
                :class="endTimeSuffix && 'sm:!max-w-[560px]'"
            >
                <div class="col-span-3 sm:col-span-2">
                    <MuvalSelect
                        placeholder="Delivery options"
                        :vid="keys.end_at_type"
                        displayLabel="label"
                        valueProp="value"
                        :selectOptions="dateOptions"
                    />
                </div>

                <div
                    v-if="endType === 'BETWEEN'"
                    class="relative col-span-3 flex"
                >
                    <MPDatetime
                        :start-vid="keys.end_at_from"
                        :end-vid="keys.end_at_by"
                        is-range
                        :teleport="teleport"
                        :time-suffix="endTimeSuffix"
                        :class="endTimeSuffix ? 'w-full' : ''"
                        :min-date="allowPastDates ? null : new Date()"
                    >
                        <template #action-extra="slotProps">
                            <slot
                                name="end-action-extra"
                                v-bind="slotProps"
                            />
                        </template>
                    </MPDatetime>
                </div>
                <template v-if="endType === 'ON'">
                    <div class="col-span-3 flex">
                        <MPDatetime
                            :vid="keys.end_at_by"
                            :teleport="teleport"
                            :time-suffix="endTimeSuffix"
                            :class="endTimeSuffix ? 'w-full' : ''"
                            :min-date="allowPastDates ? null : new Date()"
                        >
                            <template #action-extra="slotProps">
                                <slot
                                    name="end-action-extra"
                                    v-bind="slotProps"
                                />
                            </template>
                        </MPDatetime>
                    </div>
                </template>
                <template v-else-if="endType === 'UNSURE'">
                    <div class="col-span-3 flex">
                        <MuvalSelect
                            :vid="keys.end_date_interval"
                            :single-select="true"
                            :select-options="moveDateIntervals"
                        />
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import MuvalSelect from '@components/inputs/MuvalSelect.vue';
import MuvalTip from '@components/button/MuvalTip.vue';
import { useFieldValue } from 'vee-validate';
import { cloneDeep } from 'lodash';

const props = defineProps({
    isStart: {
        type: Boolean,
        default: false,
    },
    isEnd: {
        type: Boolean,
        default: false,
    },
    isBoth: {
        type: Boolean,
        default: true,
    },
    keys: {
        type: Object,
        default: () => ({
            start_at_type: 'pickup_date_type',
            start_at_from: 'pickup_date_from',
            start_at_by: 'pickup_date_by',
            end_at_type: 'delivery_date_type',
            end_at_from: 'delivery_date_from',
            end_at_by: 'delivery_date_by',
            start_date_interval: 'pickup_date_interval',
            start_date_flexibility: 'pickup_date_flexibility',
            end_date_interval: 'delivery_date_interval',
        }),
    },
    form: {
        type: Object,
        required: true,
    },
    initialValues: {
        type: Object,
        required: true,
    },
    dateKey: {
        type: [String, null],
        required: false,
        default: null,
    },
    dateTypes: {
        type: Array,
        required: false,
        default: () => ['ALL'],
    },
    deliveryLabel: {
        type: String,
        default: 'Ideal delivery timeframe',
    },
    noFlexibility: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideLabels: {
        type: Boolean,
        required: false,
        default: false,
    },
    allowPastDates: {
        type: Boolean,
        required: false,
        default: false,
    },
    // Teleport the internal date-picker menus to <body> so they escape
    // overflow-hidden parents (e.g. modals). Off by default to preserve
    // existing positioning for inline usages.
    teleport: {
        type: Boolean,
        required: false,
        default: false,
    },
    // Optional time-of-day text appended to the start/end date inputs (e.g. "9:00 AM – 11:00 AM")
    // so a separately-stored time stays visible while the picker is closed.
    startTimeSuffix: {
        type: [String, null],
        required: false,
        default: null,
    },
    endTimeSuffix: {
        type: [String, null],
        required: false,
        default: null,
    },
});

const { moveDateIntervals } = usePredefined();

const flexibilityOptions = [
    {
        label: 'Exact date',
        value: 'EXACT',
    },
    {
        label: '± 3 days',
        value: 'THREE_DAYS',
    },
    {
        label: '± Week',
        value: 'WEEK',
    },
    {
        label: '± Fortnight',
        value: 'FORTNIGHT',
    },
];

const dateOptions = computed(() => {
    if (props.dateTypes.includes('ALL')) {
        return [
            { label: 'Between', value: 'BETWEEN' },
            { label: 'On', value: 'ON' },
            { label: 'Unsure', value: 'UNSURE' },
        ];
    } else {
        return [
            { label: 'Between', value: 'BETWEEN' },
            { label: 'On', value: 'ON' },
        ];
    }
});

const startType = useFieldValue(props.keys.start_at_type);
const endType = useFieldValue(props.keys.end_at_type);

// make a copy of the initialValues
const _initialValues = cloneDeep(props.initialValues);

// watch startType
watch(startType, (val) => {
    if (val == 'BETWEEN') {
        props.form.setFieldValue(props.keys.start_at_from, _initialValues.pickup_date_from);
        props.form.setFieldValue(props.keys.start_at_by, _initialValues.pickup_date_by);
        props.form.setFieldValue(props.keys.start_at_type, 'BETWEEN');
    } else if (val == 'ON') {
        props.form.setFieldValue(props.keys.start_at_by, _initialValues.pickup_date_by);
        props.form.setFieldValue(props.keys.start_at_type, 'ON');
        if (!props.noFlexibility) {
            props.form.setFieldValue(
                props.keys.start_date_flexibility,
                _initialValues.pickup_date_flexibility ?? 'EXACT',
            );
        }
    } else if (val == 'UNSURE') {
        props.form.setFieldValue(props.keys.start_at_type, 'UNSURE');
        props.form.setFieldValue(props.keys.start_date_interval, _initialValues.pickup_date_interval ?? 'THREE_MONTH');
    }
});
</script>
